/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck

import { isMobile } from 'react-device-detect';
import { type Texture } from 'three';

import { type NFTTexture } from '@pkg/webgl/components/YatCube/index';
import { FACE_NAMES } from '@pkg/webgl/shared';

import { type TokenV2Metadata } from '@app/client';

import { asyncLoader } from '../../utils';
import Loaders from '../../utils/Loaders';
import { VideoTexture } from '../../utils/VideoTexture';

export default class AssetsManager {
    // Used to keep track of loaded textures, keys are nft id
    loadedNFTTextures: Record<string, Texture>;
    hasVideoTexture: boolean;
    allowedVideos: boolean;
    activeTextures: NFTTexture[];

    constructor() {
        this.hasVideoTexture = false;
        this.loadedNFTTextures = {};
        this.activeTextures = [];
        this.allowedVideos = true;
    }

    private async dynamicTextureLoad(assetPath: string, objectName: string, faceName, yatCube) {
        const texture = (await asyncLoader(assetPath, Loaders.textureLoader)) as Texture;
        if (!texture) return;

        texture.flipY = false;
        texture._width = texture.image.width;
        texture._height = texture.image.height;
        this.loadedNFTTextures[objectName] = texture;

        // console.log('transition face from assets manager');
        yatCube.updateFaceUniforms(texture, faceName);
        yatCube.transitionFace({ faceName: faceName }, 'in');
    }

    private async dynamicVideoTextureLoad(assetPath: string, objectName: string, faceName, yatCube, fallbackImage) {
        const video = document.createElement('video');
        video.setAttribute('crossOrigin', 'anonymous');
        video.setAttribute('webkit-playsinline', 'webkit-playsinline');
        video.setAttribute('playsinline', 'playsinline');
        video.autoplay = true;
        video.muted = true;
        video.loop = true;

        const texture = new VideoTexture(video);

        video.addEventListener('loadedmetadata', () => {
            // Update aspectRatio
            yatCube.updateFaceUniforms(texture, faceName);
        });

        texture.needsUpdate = false;
        texture._isPlayable = false;

        const canPlayThrough = () => {
            // Animate only once video is Playable
            texture._isPlayable = true;
            texture.needsUpdate = true;
            video.removeEventListener('canplaythrough', canPlayThrough);
            this.loadedNFTTextures[objectName] = texture;
            this.hasVideoTexture = true;

            setTimeout(() => {
                if (fallbackImage) yatCube.replaceFallbackImage(texture, faceName);
                yatCube.updateFaceUniforms(texture, faceName);
                if (!fallbackImage) yatCube.transitionFace({ faceName: faceName }, 'in');
            }, 50);
        };
        video.addEventListener('canplaythrough', canPlayThrough);

        video.addEventListener('error', () => {
            if (this.loadedNFTTextures[objectName] && !fallbackImage) delete this.loadedNFTTextures[objectName];
        });

        video.src = assetPath;

        if (isMobile) {
            // Check if autoplay is available, otherwise use any click to play the video
            video
                .play()
                .then(() => {
                    video.pause();
                })
                .catch(() => {
                    const checkClick = () => {
                        try {
                            video.play();
                            document.body.removeEventListener('click', checkClick);
                        } catch {
                            // eslint-disable-next-line no-console
                            console.error('Autoplay error');
                        }
                    };
                    document.body.addEventListener('click', checkClick);
                });
        }

        this.hasVideoTexture = true;

        if (fallbackImage) {
            await this.loadFallbackImage(fallbackImage, objectName, faceName, texture, yatCube);
        } else {
            this.loadedNFTTextures[objectName] = texture;
        }
    }

    async loadFallbackImage(assetPath, objectName, faceName, videoTexture, yatCube) {
        const texture = (await asyncLoader(assetPath, Loaders.textureLoader)) as Texture;

        // In case video is playable before the fallback texture is loaded
        if (videoTexture._isPlayable) {
            this.loadedNFTTextures[objectName] = videoTexture;
            yatCube.updateFaceUniforms(videoTexture, faceName);
            // console.log('show video');
            yatCube.transitionFace({ faceName: faceName }, 'in');
            if (texture) texture.dispose();

            return;
        }

        if (!texture) return;

        texture.flipY = false;
        texture._width = texture.image.width;
        texture._height = texture.image.height;

        this.loadedNFTTextures[objectName] = texture;
        yatCube.updateFaceUniforms(texture, faceName);
        yatCube.transitionFace({ faceName: faceName }, 'in');
        // console.log('show fallback image');
    }

    async checkTextures(items: TokenV2Metadata[], yatCube) {
        const activeTextures: NFTTexture[] = [];

        // Will be set to true if video
        this.hasVideoTexture = false;

        let itemIndex = -1;
        const promises = [];

        // This is ugly but forEach doesn't work nicely with async/await
        for (const { index, value } of FACE_NAMES.map((value, index) => ({ index, value }))) {
            const item = items[index];

            if (item) {
                itemIndex += 1;
                const id = item.queryFormat;

                let nftImage = item?.image_url;
                let nftVideo = item?.animation_url;

                if (nftVideo && !this.allowedVideos) {
                    nftImage = false;
                    nftVideo = false;
                }

                if (this.loadedNFTTextures[id]) {
                    if (this.loadedNFTTextures[id].isVideoTexture) this.hasVideoTexture = true;
                    setTimeout(() => {
                        if (this.loadedNFTTextures[id].isVideoTexture) {
                            if (this.loadedNFTTextures[id]._isPlayable)
                                yatCube.transitionFace({ faceName: value }, 'in');
                        } else {
                            yatCube.transitionFace({ faceName: value }, 'in');
                        }
                    }, 50);
                } else {
                    if (nftImage || nftVideo) {
                        if (nftVideo) {
                            this.hasVideoTexture = true;
                            const fallbackImage = nftImage || false;
                            promises.push(this.dynamicVideoTextureLoad(nftVideo, id, value, yatCube, fallbackImage));
                        } else {
                            promises.push(this.dynamicTextureLoad(nftImage, id, value, yatCube));
                        }
                    }
                }
                activeTextures[itemIndex] = { faceName: value, id: id };
            }
        }
        // Load assets in parallels
        await Promise.all(promises);

        Object.keys(activeTextures).forEach((key) => {
            if (this.loadedNFTTextures[activeTextures[key].id]) {
                activeTextures[key].asset = this.loadedNFTTextures[activeTextures[key].id];
            } else {
                delete activeTextures[key];
            }
        });

        this.activeTextures = activeTextures;

        return activeTextures;
    }

    cleanTextures() {
        Object.keys(this.loadedNFTTextures).forEach((key) => {
            this.loadedNFTTextures[key].dispose();
        });
        this.loadedNFTTextures = {};
    }
}
